import React, { useState } from "react";
import { Transaction } from "../../../domain/types/Transaction";
import { TransactionCard } from "./TransactionCard";
import { TransactionDetails } from "./TransactionDetails";
import { BankAccount } from "../../../domain/types/banks/BankAccount";
import { Bank } from "../../../domain/types/banks/Bank";
import { groupTransactions } from "../utils/transactionFormatters";
import { HiX } from "react-icons/hi";

interface TransactionTableProps {
  transactions: Transaction[];
  groupedBy: "date" | "merchant" | "category" | "none";
  onSelectionChange: (transactions: Transaction[]) => void;
  accountsById: { [accountId: string]: BankAccount };
  banksById: { [bankId: string]: Bank };
}

export const TransactionTable: React.FC<TransactionTableProps> = ({
  transactions,
  groupedBy,
  onSelectionChange,
  accountsById,
  banksById,
}) => {
  const [selectedTransactions, setSelectedTransactions] = useState<
    Map<string, Transaction>
  >(new Map());
  const [activeTransaction, setActiveTransaction] =
    useState<Transaction | null>(null);
  const [activeAccount, setActiveAccount] = useState<BankAccount | null>(null);
  const [activeBank, setActiveBank] = useState<Bank | null>(null);

  const handleSelect = (transaction: Transaction, checked: boolean) => {
    const newSelected = new Map(selectedTransactions);
    if (checked) {
      newSelected.set(transaction.transaction_id, transaction);
    } else {
      newSelected.delete(transaction.transaction_id);
    }
    setSelectedTransactions(newSelected);
    onSelectionChange(Array.from(newSelected.values()));
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelected = new Map<string, Transaction>();
      transactions.forEach((transaction) => {
        newSelected.set(transaction.transaction_id, transaction);
      });
      setSelectedTransactions(newSelected);
      onSelectionChange(Array.from(newSelected.values()));
    } else {
      setSelectedTransactions(new Map());
      onSelectionChange([]);
    }
  };

  const handleTransactionClick = (transaction: Transaction) => {
    const account = accountsById[transaction.account_id] || null;
    const bank = account?.bankId ? banksById[account.bankId] : null;

    setActiveTransaction(transaction);
    setActiveAccount(account);
    setActiveBank(bank);
  };

  // Group transactions based on the 'groupedBy' prop
  const groupedTransactions = groupTransactions(transactions, groupedBy);

  return (
    <div>
      {/* Select All Checkbox */}
      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={selectedTransactions.size === transactions.length}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="form-checkbox h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {selectedTransactions.size === 0
              ? "Select All Transactions"
              : `Selected ${selectedTransactions.size} of ${transactions.length} transactions`}
          </span>
        </div>

        {selectedTransactions.size > 0 && (
          <button
            onClick={() => {
              setSelectedTransactions(new Map());
              onSelectionChange([]);
            }}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center space-x-1"
          >
            <span>Clear Selection</span>
            <HiX className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Transactions List */}
      <div className="space-y-6">
        {Object.entries(groupedTransactions).map(
          ([group, groupTransactions]) => (
            <div key={group}>
              {groupedBy !== "none" && (
                <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-md mb-2">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    {group} • {groupTransactions.length} transaction
                    {groupTransactions.length > 1 ? "s" : ""}
                  </h2>
                </div>
              )}
              <div className="space-y-4">
                {groupTransactions.map((transaction) => {
                  const account = accountsById[transaction.account_id] || null;
                  const bank = account?.bankId
                    ? banksById[account.bankId]
                    : undefined;

                  return (
                    <TransactionCard
                      key={transaction.transaction_id}
                      transaction={transaction}
                      account={account}
                      bank={bank}
                      isSelected={selectedTransactions.has(
                        transaction.transaction_id,
                      )}
                      onSelect={handleSelect}
                      onClick={() => handleTransactionClick(transaction)}
                    />
                  );
                })}
              </div>
            </div>
          ),
        )}
      </div>

      {/* Transaction Details Modal */}
      {activeTransaction && (
        <TransactionDetails
          transaction={activeTransaction}
          account={activeAccount}
          bank={activeBank}
          onClose={() => {
            setActiveTransaction(null);
            setActiveAccount(null);
            setActiveBank(null);
          }}
        />
      )}
    </div>
  );
};
